import { LoadingButton } from "@mui/lab"
import { Box, Divider, Link, Stack, Typography } from "@mui/material"
import { useSuspenseQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { generateRandomString } from "@sap/utils/random"

import { getAuth0Login, getMicrosoftLogin } from "@/api/index"
import { auth0LoginKeys, microsoftLoginKey } from "@/api/queryKeys"
import Page from "@/components/Page"
import { useAuthStore } from "@/store/auth"

export default function Login(): JSX.Element {
  const { t } = useTranslation("login")
  const [navigating, setNavigating] = useState(false)
  const setLoginState = useAuthStore((state) => state.setLoginState)

  const { data: microsoft_data, isError } = useSuspenseQuery({
    queryKey: microsoftLoginKey,
    queryFn: ({ signal }) => getMicrosoftLogin(signal, window.location.host),
  })

  const { data: auth0_data, isError: auth0_isError } = useSuspenseQuery({
    queryKey: auth0LoginKeys.login,
    queryFn: ({ signal }) => getAuth0Login(signal, window.location.host),
  })

  function handleLogin(e: React.MouseEvent, url: string | undefined): void {
    if (!url) return

    setNavigating(true)
    e.preventDefault()
    const state = generateRandomString(20)
    const Url = new URL(url)
    Url.searchParams.append("state", state)
    const newUrl = `${Url.origin}${Url.pathname}${Url.search}`
    setLoginState({ state, redirect: "/" })
    window.location.href = newUrl
  }

  useEffect(() => {
    window.onpageshow = (event) => {
      if (event.persisted) {
        // reset the navigating state on hit bfcache
        setNavigating(false)
      }
    }
  }, [])

  return (
    <Page title="Login">
      <Stack
        spacing={2}
        sx={{
          alignItems: "stretch",
          mb: 2,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h4">{t("Welcome")}</Typography>
          <Typography variant="body2">{t("LoginPriceList")}</Typography>
        </Box>
        <LoadingButton
          variant="contained"
          fullWidth
          size="large"
          loading={navigating}
          disabled={auth0_isError}
          component={Link}
          href={auth0_data?.auth_uri}
          onClick={(e) => {
            handleLogin(e, auth0_data?.auth_uri)
          }}
        >
          <span>{t("Login")}</span>
        </LoadingButton>
        <Divider
          sx={{
            my: 2.5,
            typography: "overline",
            color: "text.disabled",
            width: "100%",
          }}
        >
          OR
        </Divider>
        <LoadingButton
          variant="soft"
          fullWidth
          size="large"
          color="inherit"
          loading={navigating}
          disabled={isError}
          component={Link}
          href={microsoft_data?.auth_uri}
          onClick={(e) => {
            handleLogin(e, microsoft_data?.auth_uri)
          }}
        >
          <span>{t("EmployeLogin")}</span>
        </LoadingButton>
      </Stack>
    </Page>
  )
}
